import React, { useEffect, useState, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "../Actions/axios.config";
import AlertBox from "../components/Popups/AlertBox";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import LoadingSpinner from "../components/Loading/LoadingSpinner";
import { HiCheck, HiOutlineEye, HiOutlinePencil, HiOutlineSparkles, HiArrowPath } from "react-icons/hi2";
import { useCreatePost, useUpdatePost } from "../hooks/queries/usePosts";

function CreatePost() {
  const navigate = useNavigate();
  const { postId } = useParams();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useSelector((state) => state.user);

  const initialScope = searchParams.get("scope") === "campus" && isAuthenticated ? "MyCampus" : "Global";
  const quillRef = useRef(null);

  const [isPreview, setIsPreview] = useState(false); // Preview Toggle
  const [showAlert, setShowAlert] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showDraftToast, setShowDraftToast] = useState(false);
  const [collegeError, setCollegeError] = useState(false);
  const [collegeName, setCollegeName] = useState(user?.collegeName || "");

  const [formData, setFormData] = useState({
    caption: "",
    companyName: "",
    role: "",
    type: "Full Time",
    status: "Selected",
    content: "",
    visibility: initialScope,
  });

  const DRAFT_KEY = postId ? `draft_edit_${postId}` : "draft_new_post";

  // Fetch post data when in edit mode
  useEffect(() => {
    if (postId) {
      const fetchPostData = async () => {
        try {
          const response = await axios.get(`/posts/getPosts/${postId}`);
          const post = response.data.data || response.data.post || response.data;
          setFormData({
            caption: post.caption || "",
            companyName: post.company|| "",
            role: post.role || "",
            type: post.type|| "Full Time",
            status: post.status || "Selected",
            content: post.content || "",
            visibility: post.visibility || initialScope,
          });
          setCollegeName(post.collegeId?.name || "");
        } catch (error) {
          console.error("Error fetching post:", error);
          setMessage("Error fetching post data");
          setShowAlert(true);
        }
      };
      fetchPostData();
    }
  }, [postId, initialScope]);

  // Update college name when user state changes
  useEffect(() => {
    setCollegeName(user?.collegeName || "");
  }, [user?.collegeName]);

  // Auto-Save & Draft Logic
  useEffect(() => {
    const savedDraft = localStorage.getItem(DRAFT_KEY);
    if (savedDraft && !postId) setShowDraftToast(true);
  }, [DRAFT_KEY, postId]);

  useEffect(() => {
    if (formData.content.length > 20 || formData.caption.length > 5) {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(formData));
    }
  }, [formData, DRAFT_KEY]);

  const restoreDraft = () => {
    setFormData(JSON.parse(localStorage.getItem(DRAFT_KEY)));
    setShowDraftToast(false);
  };

  const handleChange = (name, value) => {
    // Validate college name when changing to MyCampus visibility
    if (name === "visibility" && value === "MyCampus" && !collegeName) {
      setCollegeError(true);
      return;
    }
    
    // Clear error when changing away from MyCampus
    if (name === "visibility" && value !== "MyCampus") {
      setCollegeError(false);
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Template Inserter
  const insertTemplate = (html) => {
    const editor = quillRef.current.getEditor();
    const range = editor.getSelection(true);
    editor.clipboard.dangerouslyPasteHTML(range.index, html);
    setIsPreview(false); // Switch back to edit if they insert a template
  };

  const createPostMutation = useCreatePost();
  const updatePostMutation = useUpdatePost(postId);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (postId) {
        await updatePostMutation.mutateAsync(formData);
        setMessage("Post Updated Successfully");
      } else {
        await createPostMutation.mutateAsync(formData);
        setMessage("Post Created Successfully");
      }
      localStorage.removeItem(DRAFT_KEY);
      setShowAlert(true);
      setTimeout(() => {
        navigate("/posts");
      }, 1500);
    } catch (err) {
      setMessage(err.response?.data?.message || "Error saving post");
      setShowAlert(true);
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-[#f8fafc] min-h-screen pb-32 font-sans selection:bg-blue-100">
      <style>{`
        .ql-editor { font-size: 1.15rem; line-height: 1.9; color: #334155; min-height: 500px; padding: 0 !important; }
        .ql-editor.ql-blank::before { color: #cbd5e1; font-style: normal; font-weight: 500; }
        .ql-editor h3 { font-size: 1.5rem; font-weight: 800; color: #0f172a; margin-top: 2rem; }
        .ql-container.ql-snow { border: none !important; }
        
        .ql-toolbar.ql-snow { 
          border: none !important; 
          border-bottom: 1px solid #f1f5f9 !important; 
          position: sticky; 
          top: 0; 
          background: white; 
          z-index: 30; 
          padding: 12px 0 !important;
          margin-bottom: 24px;
        }
        
        .ql-toolbar.ql-snow button { 
          width: 32px !important; 
          height: 32px !important; 
          border-radius: 8px !important; 
          transition: all 0.2s ease;
          margin-right: 2px;
        }
        .ql-toolbar.ql-snow button:hover { background-color: #f1f5f9 !important; color: #2563eb !important; }
        .ql-toolbar.ql-snow button.ql-active { background-color: #eff6ff !important; color: #2563eb !important; }
        .ql-toolbar.ql-snow button.ql-active .ql-stroke { stroke: #2563eb !important; }
        .ql-toolbar.ql-snow button.ql-active .ql-fill { fill: #2563eb !important; }
        .ql-toolbar.ql-snow .ql-stroke { stroke-width: 1.5px !important; }

        .preview-content h3 { font-size: 1.5rem; font-weight: 800; margin-top: 2rem; margin-bottom: 1rem; }
        .preview-content blockquote { border-left: 4px solid #3b82f6; padding-left: 1.5rem; background: #f0f7ff; padding: 1rem 1.5rem; border-radius: 8px; }
      `}</style>

      {/* Top Floating Nav */}
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-200 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsPreview(!isPreview)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all bg-slate-100 hover:bg-slate-200 text-slate-700"
            >
              {isPreview ? <><HiOutlinePencil /> Edit Mode</> : <><HiOutlineEye /> Preview</>}
            </button>
            {showDraftToast && (
              <button onClick={restoreDraft} className="text-blue-600 text-xs font-bold hover:underline flex items-center gap-1">
                <HiArrowPath /> Restore Draft
              </button>
            )}
          </div>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-2.5 rounded-full font-bold shadow-lg shadow-blue-100 flex items-center gap-2 transition-transform active:scale-95"
          >
            {loading ? <LoadingSpinner size="sm" /> : <><HiCheck /> Publish</>}
          </button>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 mt-12">
        <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-8 md:p-16">
            
            {/* Title Area */}
            <div className="mb-12">
              <textarea
                placeholder="Post Title..."
                rows="1"
                value={formData.caption}
                onChange={(e) => handleChange("caption", e.target.value)}
                onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 300) + 'px'; }}
                className="w-full text-5xl font-black text-slate-900 placeholder:text-slate-300 border-none outline-none focus:ring-0 p-0 bg-transparent resize-none leading-tight break-words"
                style={{ fontFamily: 'inherit', overflowWrap: 'break-word', whiteSpace: 'pre-wrap' }}
              />
            </div>

            {/* Metadata Section */}
            {!isPreview && (
              <div className="space-y-6 mb-12 pt-8 border-t border-slate-100">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-slate-50/50 p-5 rounded-2xl border border-slate-200 focus-within:ring-2 focus-within:ring-blue-300 focus-within:border-transparent transition-all">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">Target Company</label>
                    <input type="text" placeholder="e.g. Amazon" value={formData.companyName} onChange={(e) => handleChange("companyName", e.target.value)} className="w-full bg-transparent font-bold text-lg outline-none text-slate-800 placeholder:text-slate-400" />
                  </div>
                  <div className="bg-slate-50/50 p-5 rounded-2xl border border-slate-200 focus-within:ring-2 focus-within:ring-blue-300 focus-within:border-transparent transition-all">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">Applied Role</label>
                    <input type="text" placeholder="e.g. Backend Engineer" value={formData.role} onChange={(e) => handleChange("role", e.target.value)} className="w-full bg-transparent font-bold text-lg outline-none text-slate-800 placeholder:text-slate-400" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 bg-slate-50/50 px-5 py-4 rounded-2xl border border-slate-200 focus-within:ring-2 focus-within:ring-blue-300 focus-within:border-transparent transition-all">
                    <span className="text-xs font-bold text-slate-500 uppercase mb-2 sm:mb-0">Type:</span>
                    <select value={formData.type} onChange={(e) => handleChange("type", e.target.value)} className="bg-transparent font-bold text-slate-700 outline-none cursor-pointer w-full sm:w-auto">
                      <option>Full Time</option>
                      <option>Intern</option>
                    </select>
                  </div>
                  <div className={`flex flex-col sm:flex-row sm:items-center sm:gap-4 px-5 py-4 rounded-2xl border transition-all focus-within:ring-2 focus-within:ring-blue-300 focus-within:border-transparent ${formData.status === 'Selected' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                    <span className="text-xs font-bold opacity-70 uppercase mb-2 sm:mb-0">Status:</span>
                    <select value={formData.status} onChange={(e) => handleChange("status", e.target.value)} className="bg-transparent font-bold outline-none cursor-pointer w-full sm:w-auto">
                      <option>Selected</option>
                      <option>Not Selected</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 bg-slate-50/50 px-5 py-4 rounded-2xl border border-slate-200 focus-within:ring-2 focus-within:ring-blue-300 focus-within:border-transparent transition-all">
                    <span className="text-xs font-bold text-slate-500 uppercase mb-2 sm:mb-0">Visibility:</span>
                    <select
                      value={isAuthenticated ? formData.visibility : "Global"}
                      onChange={(e) => handleChange("visibility", e.target.value)}
                      className="bg-transparent font-bold text-slate-700 outline-none cursor-pointer w-full sm:w-auto disabled:opacity-50"
                      title={!isAuthenticated ? "Login to post to MyCampus" : ""}
                    >
                      <option value="Global">Global</option>
                      {isAuthenticated && <option value="MyCampus">MyCampus</option>}
                    </select>
                  </div>
                  {collegeError && (
                    <div className="bg-red-50 border border-red-200 rounded-2xl p-5 text-sm shadow-sm">
                      <p className="text-red-700 font-semibold mb-3">⚠️ Please add your college name to post to MyCampus</p>
                      <a href="/profile" className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 font-bold text-sm bg-red-100 px-3 py-2 rounded-lg hover:bg-red-200 transition-colors">Go to Edit Profile →</a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Editor or Preview Toggle */}
            <div className="pt-8 border-t border-slate-100">
              {isPreview ? (
                <div className="preview-content prose prose-slate max-w-none animate-in fade-in duration-500">
                  <div dangerouslySetInnerHTML={{ __html: formData.content }} />
                  {formData.content.length < 10 && <p className="text-slate-300 italic">Your story is empty. Switch back to edit mode to start writing...</p>}
                </div>
              ) : (
                <div className="quill-custom animate-in fade-in duration-300">
                  <ReactQuill
                    ref={quillRef}
                    value={formData.content}
                    onChange={(val) => handleChange("content", val)}
                    modules={quillModules}
                    theme="snow"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {!isPreview && (
        <div className="fixed bottom-10 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-2 py-2 md:px-6 md:py-3 rounded-2xl shadow-2xl flex items-center gap-2 md:gap-6 border border-white/10 z-50 animate-in slide-in-from-bottom-10 max-w-[90vw] overflow-x-auto">
          <div className="flex items-center gap-2 text-blue-400 text-xs font-bold uppercase tracking-tighter pr-4 border-r border-white/10 shrink-0">
            <HiOutlineSparkles size={16} /> <span className="hidden sm:inline">Quick Add</span>
          </div>
          <button onClick={() => insertTemplate(`<h3>🔵 Round 1: [Type]</h3><p><strong>Focus:</strong> DSA/System Design</p><ul><li>Question 1...</li></ul>`)} className="hover:text-blue-400 transition-colors text-sm font-bold shrink-0 whitespace-nowrap">Round</button>
          <button onClick={() => insertTemplate(`<blockquote>💡 <strong>Key Tip:</strong> Focus on...</blockquote>`)} className="hover:text-yellow-400 transition-colors text-sm font-bold shrink-0 whitespace-nowrap">Pro-Tip</button>
          <button onClick={() => insertTemplate(`<h3>📚 Resources</h3><ul><li>[Link Name]</li></ul>`)} className="hover:text-purple-400 transition-colors text-sm font-bold shrink-0 whitespace-nowrap">Prep-Link</button>
        </div>
      )}

      {showAlert && <AlertBox message={message} status="true" onClose={() => navigate("/posts")} />}
    </div>
  );
}

const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image"],
    ["clean"]
  ]
};

export default CreatePost;